// @ts-nocheck
import { compact, debounce } from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  ButtonUI,
  InputSearch,
  showNotification,
  TableListV2 as TableListTS,
  useQueryFetch
} from 'sonata-react-components';

import { getStaticMode } from '@/store/selectors/reporting.selectors';
import { downloadFileFromUrl } from '@/ts-helpers/fileDownload.helper';
import { deleteReportingSchedule, updateReportingSchedule } from '../../../reporting.resources';
import { DeleteReportPrompt } from '../DeleteReportPrompt';
import { ScheduledReportsTabStyle } from './ScheduledReportsTab.style';

import type { ScheduleReportInterfaceWithAdditionalData } from 'sonata-static-storage/lib/schemas/scheduleReport/scheduleReport.schema';

const MILLISECONDS_MAX_ERROR = 5 * 1000;

type Props = {
  campaignsIds: string[];
  id: string;
  isDownloadLoading?: boolean;
  onDownloadMultipleReports: (ids: string[]) => void;
  onReportLoad: (report: ScheduleReportInterfaceWithAdditionalData) => void;
  userId: string;
};

type State = {
  cacheSeed: string;
  idToChangeState?: {
    id: string;
    newState: boolean;
  };
  idToDelete?: string;
  selectedReports: ScheduleReportInterfaceWithAdditionalData[];
  textSearch?: string;
};

const DATE_FORMAT = 'DD MMM, YYYY, hh:mm A';

function ScheduledReportsTab({
  campaignsIds,
  id,
  isDownloadLoading = false,
  onDownloadMultipleReports,
  onReportLoad,
  userId
}: Props) {
  const useBackups = useSelector(getStaticMode);
  const [{ cacheSeed, idToChangeState, idToDelete, selectedReports, textSearch }, setState] = useState<State>({
    cacheSeed: new Date().toISOString(),
    selectedReports: []
  });
  const { formatMessage } = useIntl();

  const { isFetching: isDeleteFetching, refetch: refetchDelete } = useQueryFetch(
    ['scheduleReportDelete', idToDelete],
    async () => deleteReportingSchedule(idToDelete!),
    {
      enabled: false,
      onError: () => {
        setState(prevState => ({
          ...prevState,
          idToDelete: undefined
        }));
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'Deletion failed',
            id: 'reporting.reportingDownload.myReportsModal.deletionFailed'
          })
        );
      },
      onSuccess: () => {
        setState(prevState => ({
          ...prevState,
          cacheSeed: new Date().toISOString(),
          idToDelete: undefined
        }));
      }
    }
  );

  const { isFetching: isChangeStateFetching, refetch: refetchChangeState } = useQueryFetch(
    ['scheduleReportChangeState', idToChangeState],
    async () => updateReportingSchedule(idToChangeState!.id, { enable: idToChangeState!.newState }),
    {
      enabled: false,
      onError: () => {
        setState(prevState => ({
          ...prevState,
          idToChangeState: undefined
        }));
        showNotification(
          'error',
          formatMessage({
            defaultMessage: 'Failed to change status',
            id: 'reporting.reportingDownload.myReportsModal.changeStatusFailed'
          })
        );
      },
      onSuccess: () => {
        setState(prevState => ({
          ...prevState,
          cacheSeed: new Date().toISOString(),
          idToChangeState: undefined
        }));
      }
    }
  );

  useEffect(() => {
    if (idToChangeState) {
      refetchChangeState();
    }
  }, [idToChangeState, refetchChangeState]);

  const toolbar = {
    gridConfig: [
      {
        className: 'reportingDownloadsTitle',
        description: formatMessage({
          defaultMessage: 'Name',
          id: 'reporting.reportingDownload.myReportsModal.scheduledReportsTab.table.header.name'
        }),
        key: 'name'
      },
      {
        className: 'reportingDownloadsSubTitle',
        description: formatMessage({
          defaultMessage: 'Last version',
          id: 'reporting.reportingDownload.myReportsModal.scheduledReportsTab.table.header.lastVersion'
        }),
        key: 'lastVersion'
      },
      {
        className: 'reportingDownloadsFileType',
        description: formatMessage({
          defaultMessage: 'Type',
          id: 'reporting.reportingDownload.myReportsModal.scheduledReportsTab.table.header.fileType'
        }),
        key: 'fileType'
      },
      {
        className: 'reportingDownloadsScheduled',
        description: formatMessage({
          defaultMessage: 'Scheduled',
          id: 'reporting.reportingDownload.myReportsModal.scheduledReportsTab.table.header.scheduled'
        }),
        key: 'scheduled'
      }
    ],
    gridLayout: 'reportingScheduledReportsToolbarLayout',
    showSelectAll: true
  };

  const defaultEmptyState = {
    description: formatMessage({
      defaultMessage: "Explore our content and select what you'd like to download. Start now and find what you need",
      id: 'reporting.reportingDownload.myReportsModal.listEmptyStyle.description'
    }),
    fontSizes: {
      description: '12px',
      icon: '18px',
      title: '14px'
    },
    icon: 'fal fa-bolt',
    show: true,
    spacing: 'small',
    title: formatMessage({
      defaultMessage: 'You don’t have any downloads yet',
      id: 'reporting.reportingDownload.myReportsModal.listEmptyStyle.title'
    })
  };

  const searchDefaultEmptyState = {
    fontSizes: {
      description: '12px',
      icon: '18px',
      title: '14px'
    },
    icon: 'fal fa-empty-set',
    show: true,
    spacing: 'small',
    title: formatMessage({
      defaultMessage: 'There is no data for the current search.',
      id: 'reporting.reportingDownload.myReportsModal.searchEmptyStyle.title'
    })
  };

  const handleSelectionChange = (rows: { entity: ScheduleReportInterfaceWithAdditionalData }[]) => {
    setState(prevState => ({
      ...prevState,
      selectedReports: rows.map(r => r.entity)
    }));
  };

  const handleSearchInputChange = debounce(newText => {
    setState(prevState => ({
      ...prevState,
      textSearch: newText
    }));
  }, 500);

  const handleDownloadMultipleReports = () => {
    onDownloadMultipleReports(compact(selectedReports.map(report => report.lastDownload._id)));
  };

  const handleNewFromReportClick = (schedule: ScheduleReportInterfaceWithAdditionalData) => {
    onReportLoad(schedule);
  };

  const handleDeleteScheduledReportClick = (selectedId: string) => {
    setState(prevState => ({
      ...prevState,
      idToDelete: selectedId
    }));
  };

  const handleDownloadReportClick = (report: ScheduleReportInterfaceWithAdditionalData) => {
    if (report.lastDownload) {
      downloadFileFromUrl(report.lastDownload.s3Url);
    }
  };

  const handleToggleEnableClick = (selectedId: string, enabled: boolean) => {
    setState(prev => ({
      ...prev,
      idToChangeState: {
        id: selectedId,
        newState: enabled
      }
    }));
  };

  const handlePromptClose = () => {
    setState(prev => ({
      ...prev,
      idToDelete: undefined
    }));
  };

  const cacheSeedWithFilters = `scheduleReports_${cacheSeed}_${textSearch}`;

  const getNextScheduleExecution = (schedule: ScheduleReportInterfaceWithAdditionalData): string => {
    const hours = (schedule.hours || []).reduce(
      (prev, hour) => [...prev, ...(!isNaN(parseInt(hour)) ? [parseInt(hour)] : [])],
      [] as number[]
    );
    hours.sort((a, b) => a - b);
    if (
      hours &&
      hours.length &&
      schedule.lastDownload &&
      new Date(schedule.modified).getTime() - new Date(schedule.lastDownload.created).getTime() > 5000
    ) {
      const nextScheduleMoment = moment().tz(schedule.timezone);
      let nextHour = hours.find(hour => hour > nextScheduleMoment.hour());
      switch (schedule.periodicity) {
        case 'daily':
          if (nextHour === undefined) {
            nextHour = hours[0];
            nextScheduleMoment.add(1, 'day');
          }
          break;
        case 'monthly':
          if (nextScheduleMoment.date() !== schedule.monthDay || nextHour === undefined) {
            nextHour = hours[0];
            nextScheduleMoment.date() >= schedule.monthDay!
              ? nextScheduleMoment.add(1, 'month').date(schedule.monthDay!)
              : nextScheduleMoment.date(schedule.monthDay!);
          }
          break;
        case 'weekly':
          if (nextHour === undefined || nextScheduleMoment.day() !== schedule.weekDay) {
            const currentDay = nextScheduleMoment.day();
            const daysToAdd = currentDay === schedule.weekDay ? 7 : (schedule.weekDay! + 7 - currentDay) % 7;
            nextScheduleMoment.add(daysToAdd, 'days');
            nextHour = hours[0];
          }
          break;
        default:
          nextHour = hours[0];
      }
      const endMoment = moment(schedule.timeFrame.end, 'YYYY-MM-DD', schedule.timezone);
      nextScheduleMoment.set('hours', nextHour);
      nextScheduleMoment.set('minutes', 0);
      nextScheduleMoment.set('seconds', 0);
      endMoment.set('hours', hours[hours.length - 1]);
      endMoment.set('minutes', 0);
      endMoment.set('seconds', 0);

      if (nextScheduleMoment.valueOf() - endMoment.valueOf() > MILLISECONDS_MAX_ERROR) {
        return '';
      }

      return nextScheduleMoment.format(DATE_FORMAT);
    }
    return '';
  };

  const config = {
    emptyState: defaultEmptyState,
    onChangeSelection: handleSelectionChange,
    pagination: {
      async: {
        formatRequestArgs: (page: number, itemsPerPage: number) => ({
          method: 'GET' as const,
          params: new URLSearchParams({
            ...(textSearch && { filterName: textSearch }),
            additionalData: 'true',
            limit: itemsPerPage.toString(),
            order: 'desc',
            page: (page + 1).toString(),
            sortBy: 'modified'
          })
        }),
        parseData: (data: { items: ScheduleReportInterfaceWithAdditionalData[] }) =>
          data?.items?.map(schedule => {
            const nextScheduleExecutionString = getNextScheduleExecution(schedule);
            return {
              disabled: !(schedule as any).lastDownload,
              entity: schedule,
              id: schedule._id,
              reportingDownloadsFileType: {
                text: formatMessage({
                  description: schedule.report?.type || 'N/A',
                  id: `reporting.reportingDownload.myReportsModal.fileTypes.${schedule.report?.type}`
                })
              },
              reportingDownloadsScheduled: {
                enabled: schedule.enable,
                entityId: schedule._id,
                hours: schedule.hours,
                isBackup: useBackups,
                isLoading: schedule._id === idToChangeState?.id && isChangeStateFetching,
                monthDay: schedule.monthDay,
                onEnableChange: handleToggleEnableClick,
                periodicity: schedule.periodicity,
                timeFrame: {
                  end: schedule.timeFrame.end,
                  start: schedule.timeFrame.start
                },
                weekDay: schedule.weekDay
              },
              reportingDownloadsSubTitle: {
                isSubTitle: true,
                text: schedule.lastDownload ? moment(schedule.lastDownload.modified).format(DATE_FORMAT) : ''
              },
              reportingDownloadsTitle: {
                text: schedule.name
              },
              ...(nextScheduleExecutionString && {
                reportingDownloadsScheduledSubTitle: {
                  highlightedText: nextScheduleExecutionString,
                  isSubTitle: true,
                  text: formatMessage({
                    defaultMessage: 'Edited: Changes will be applied on',
                    id: 'reporting.reportingDownload.myReportsModal.scheduledReportsTab.table.scheduledSubTitle'
                  })
                }
              }),
              ...(Boolean(!schedule.lastDownload) && {
                reportingDownloadsScheduledSubTitle: {
                  isSubTitle: true,
                  text: formatMessage({
                    defaultMessage: 'Downloads apply on selected date',
                    id: 'reporting.reportingDownload.myReportsModal.scheduledReportsTab.table.scheduledSubTitleNotGenerated'
                  })
                }
              }),
              reportingDownloadsActions: {
                actions: [
                  ...(schedule.lastDownload
                    ? [
                        {
                          icon: 'fa-regular fa-arrow-down-to-line',
                          id: `download-schedule-report-${schedule._id}`,
                          onClick: () => handleDownloadReportClick(schedule)
                        }
                      ]
                    : []),
                  ...(useBackups
                    ? [
                        {
                          icon: 'fa-regular fa-trash',
                          id: `delete-report-${schedule._id}`,
                          onClick: () => handleDeleteScheduledReportClick(schedule._id)
                        }
                      ]
                    : [])
                ],
                className: 'reportingActions',
                ...(!useBackups && {
                  contextualMenuProps: {
                    buttons: [
                      {
                        icon: 'fa-regular fa-pen-to-square',
                        id: `edit-report-${schedule._id}`,
                        onClick: () => handleNewFromReportClick(schedule),
                        text: formatMessage({
                          defaultMessage: 'Edit Report',
                          id: 'reporting.reportingDownload.myReportsModal.scheduledReportsTab.editReportButton'
                        })
                      },
                      {
                        icon: 'fa-regular fa-trash',
                        id: `delete-report-${schedule._id}`,
                        onClick: () => handleDeleteScheduledReportClick(schedule._id),
                        text: formatMessage({
                          defaultMessage: 'Delete Report',
                          id: 'reporting.reportingDownload.myReportsModal.scheduledReportsTab.deleteButton'
                        })
                      }
                    ],
                    id: 'reportingDownloadsSecondaryActions'
                  }
                })
              }
            };
          }) || [],
        refetchDataKey: cacheSeedWithFilters,
        url: `${taptap.config.backendUrlBase}report/scheduleReports/${userId}/${campaignsIds.join(',')}`
      },
      customSkeletonNumber: 4,
      show: true,
      type: 'compact'
    },
    searchEmptyState: searchDefaultEmptyState,
    selectableItem: true,
    toolbar,
    type: 'list'
  };

  return (
    <ScheduledReportsTabStyle className="scheduledReportsTab tabContainer" id={id}>
      <div className="tabFilters">
        <InputSearch
          id={`searchInput-${id}`}
          lens
          name="search"
          onChange={handleSearchInputChange}
          placeholder={formatMessage({
            defaultMessage: 'Search by name...',
            id: 'reporting.reportingDownload.myReportsModal.searchInputPlaceHolder'
          })}
        />
      </div>
      <div className="tabActions">
        <ButtonUI
          disabled={!selectedReports.length}
          icon="fa-regular fa-arrow-down-to-line"
          id={`downloadButton-${id}`}
          loading={isDownloadLoading}
          onClick={handleDownloadMultipleReports}
          shape="link"
          text={`${formatMessage({
            defaultMessage: 'Download Reports',
            id: 'reporting.reportingDownload.myReportsModal.scheduledReportsTab.downloadButton'
          })}${selectedReports.length ? ` (${selectedReports.length})` : ''}`}
          type="secondary"
        />
      </div>
      <TableListTS
        // @ts-expect-error
        config={config}
        gridLayout="reportingScheduledReportsLayout"
        hideSelectAllCheckbox
        hideShowSelectedFilter
        isCollapsable
        isSelectableList
        withComposition
      />
      {idToDelete && (
        <DeleteReportPrompt
          id={`deleteReportPrompt-${id}`}
          isLoading={isDeleteFetching}
          onClose={handlePromptClose}
          onConfirm={refetchDelete}
          type="schedule"
        />
      )}
    </ScheduledReportsTabStyle>
  );
}

export default ScheduledReportsTab;
